import { clientEnv } from '../config/env';

function trimTrailingSlash(url) {
  return String(url || '').replace(/\/+$/, '');
}

const WALLET_PAY_PATH_RE = /\/walletPay\/tgNo\/[^/]+\/referralCode\/[^/?#]+/i;
const WALLET_LINK_PATH_RE = /\/walletLink\/referralCode\/[^/?#]+/i;
const GID_SESSION_KEY = 'shop_game_group_id';

/** 钱包 DApp 深链接目标站点（来自 VUE_APP_DEEPLINK_BASE_URL） */
export function getDeeplinkBaseUrl() {
  return trimTrailingSlash(clientEnv.deeplinkBaseUrl);
}

/** 解析 gid 原始值（支持 Telegram 负数群 ID） */
export function parseGameGroupIdValue(raw) {
  if (raw == null || raw === '') return undefined;
  const s = decodeURIComponent(String(raw)).trim();
  const n = Number(s);
  if (Number.isFinite(n) && n !== 0) return n;
  return s || undefined;
}

export function persistGameGroupId(gameGroupId) {
  if (gameGroupId == null || gameGroupId === '') return;
  try {
    sessionStorage.setItem(GID_SESSION_KEY, String(gameGroupId));
  } catch {
    /* ignore */
  }
}

export function readPersistedGameGroupId() {
  try {
    return parseGameGroupIdValue(sessionStorage.getItem(GID_SESSION_KEY));
  } catch {
    return undefined;
  }
}

/** 从 hash 片段读取 gid（部分钱包保留 hash） */
function readGameGroupIdFromHash() {
  if (typeof window === 'undefined') return undefined;
  const hash = window.location.hash || '';
  const m = hash.match(/(?:^|[#&?])gid=([^&]+)/);
  return m ? parseGameGroupIdValue(m[1]) : undefined;
}

/**
 * 在 URL 上追加 gid。
 * walletPay / walletLink 使用路径段 /gid/{id}，避免 DApp 内嵌浏览器剥离 ?query。
 */
export function appendGameGroupId(url, gameGroupId) {
  if (!gameGroupId) return url;
  const gid = String(gameGroupId);
  const hashIdx = url.indexOf('#');
  const hash = hashIdx >= 0 ? url.slice(hashIdx) : '';
  const withoutHash = hashIdx >= 0 ? url.slice(0, hashIdx) : url;
  const qIdx = withoutHash.indexOf('?');
  const pathPart = qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash;
  const query = qIdx >= 0 ? withoutHash.slice(qIdx + 1) : '';

  if (WALLET_PAY_PATH_RE.test(pathPart) && !/\/gid\/[^/]+$/i.test(pathPart)) {
    const next = `${pathPart}/gid/${encodeURIComponent(gid)}`;
    return (query ? `${next}?${query}` : next) + hash;
  }
  if (WALLET_LINK_PATH_RE.test(pathPart) && !/\/gid\/[^/]+$/i.test(pathPart)) {
    const next = `${pathPart}/gid/${encodeURIComponent(gid)}`;
    return (query ? `${next}?${query}` : next) + hash;
  }

  const sep = query ? '&' : '?';
  const glued = query
    ? `${pathPart}?${query}${sep}gid=${encodeURIComponent(gid)}`
    : `${pathPart}?gid=${encodeURIComponent(gid)}`;
  return glued + hash;
}

/** 从 URLSearchParams 读取 code + gid */
export function readReferralFromSearch(search) {
  const params =
    search instanceof URLSearchParams
      ? search
      : new URLSearchParams(typeof search === 'string' ? search : window.location.search);
  return {
    referralCode: params.get('code') || '',
    gameGroupId: params.get('gid') || '',
  };
}

/** 从路由 path / query / 浏览器 URL / session 读取 gid */
export function getGameGroupIdFromRoute(route) {
  const fromParams = route?.params?.gid ?? route?.params?.gameGroupId;
  const fromQuery = route?.query?.gid;
  const fromSearch =
    typeof window !== 'undefined'
      ? readReferralFromSearch(window.location.search).gameGroupId
      : '';
  const fromHash = readGameGroupIdFromHash();
  const fromSession = readPersistedGameGroupId();

  const raw = fromParams ?? fromQuery ?? fromSearch ?? fromHash ?? fromSession;
  const parsed = parseGameGroupIdValue(raw);
  if (parsed != null && parsed !== '') {
    persistGameGroupId(parsed);
  }
  return parsed;
}

/** 从 payload / 路由 / URL 解析 gid */
export function resolveGameGroupId(source = {}) {
  const raw =
    source.gameGroupId ??
    source.gid ??
    source.game_group_id ??
    source?.params?.gid ??
    source?.query?.gid ??
    (typeof window !== 'undefined'
      ? readReferralFromSearch(window.location.search).gameGroupId
      : '');
  const parsed = parseGameGroupIdValue(raw);
  if (parsed != null && parsed !== '') {
    persistGameGroupId(parsed);
  }
  return parsed;
}

/** 统一解析推广上下文：code + gid */
export function resolveReferralContext(route) {
  const fromSearch = readReferralFromSearch(window.location.search);
  const query = route?.query || {};
  const gidRaw =
    route?.params?.gid ??
    query.gid ??
    fromSearch.gameGroupId ??
    readPersistedGameGroupId();
  let gameGroupId = '';
  if (gidRaw != null && gidRaw !== '') {
    const parsed = parseGameGroupIdValue(gidRaw);
    gameGroupId = parsed != null ? String(parsed) : String(gidRaw);
    persistGameGroupId(gameGroupId);
  }
  return {
    referralCode: query.code || fromSearch.referralCode || '',
    gameGroupId,
  };
}

/** 构建带 code/gid 的路由 query */
export function buildReferralQuery(referralCode, gameGroupId, extra = {}) {
  const q = { ...extra };
  if (referralCode) q.code = referralCode;
  if (gameGroupId) q.gid = String(gameGroupId);
  return q;
}

/** 订单/授权 payload 附加 gid（兼容多种字段名） */
export function attachGameGroupId(payload, gameGroupId) {
  if (!payload || !gameGroupId) return payload;
  return {
    ...payload,
    gameGroupId,
    gid: gameGroupId,
    game_group_id: gameGroupId,
  };
}

/** Vue Router 跳转 WalletPay 时保留 gid（走路径段） */
export function buildWalletPayRoute(tgNo, referralCode, gameGroupId) {
  let path = `/walletPay/tgNo/${tgNo}/referralCode/${referralCode}`;
  if (gameGroupId) {
    path += `/gid/${encodeURIComponent(String(gameGroupId))}`;
  }
  return { path };
}

/** Vue Router 跳转 WalletLink 时保留 gid（走路径段） */
export function buildWalletLinkRoute(referralCode, gameGroupId) {
  let path = `/walletLink/referralCode/${referralCode}`;
  if (gameGroupId) {
    path += `/gid/${encodeURIComponent(String(gameGroupId))}`;
  }
  return { path };
}

/**
 * 构建 /walletPay/tgNo/{no}/referralCode/{code} 完整 URL
 * gid 放在路径 /gid/{id}，兼容 ?gid= 旧链接
 */
export function buildWalletPayUrl(tgNo, referralCode, { encoded = false, gameGroupId } = {}) {
  const base = getDeeplinkBaseUrl();
  const no = encoded ? encodeURIComponent(tgNo ?? '') : String(tgNo ?? '');
  const code = encoded ? encodeURIComponent(referralCode ?? '') : String(referralCode ?? '');
  let url = `${base}/walletPay/tgNo/${no}/referralCode/${code}`;
  if (gameGroupId) {
    url += `/gid/${encodeURIComponent(String(gameGroupId))}`;
  }
  return url;
}

/** 构建 walletLink 完整 URL */
export function buildWalletLinkUrl(referralCode, gameGroupId) {
  const base = getDeeplinkBaseUrl();
  let url = `${base}/walletLink/referralCode/${encodeURIComponent(referralCode ?? '')}`;
  if (gameGroupId) {
    url += `/gid/${encodeURIComponent(String(gameGroupId))}`;
  }
  return url;
}
